/// <reference lib="dom"/>
/** @jsx hh */
/** @jsxFrag ChiFragment */
import { ChiFragment, hh } from "@chi/chi";
import { type ChiNode, useMemo } from "@chi/chi/hooks";
import type { ParamDict, ParamDictProps, ParamList } from "@utils/params";
import { DryWet, Feedback, Lfo, Split, useId } from "@chi/wbax";
import { SynthOpts } from "@/synth/synth.tsx";
import { BiquadTypes } from "@/synth/blocks.tsx";

export interface EffectProps {
  assets?: Record<string, AudioBuffer>;
}

export interface EffectDef<P extends ParamDict = ParamDict> {
  label: string;
  params: P;
  Component: (props: Partial<ParamDictProps<P>> & EffectProps, children: ChiElem) => ChiNode;
  worklets?: string[];
  assets?: Record<string, string>;
}

export const DbToLinear = (db: number) => Math.exp(0.11512925464970229 * db);

//#region gain
export const GainEffectParams = {
  gain: { type: "float", value: 1, min: 0, max: 20, automatable: true },
} satisfies ParamDict;

export const GainEffect = (
  props: Partial<ParamDictProps<typeof GainEffectParams>> & EffectProps,
  children: ChiElem,
) => <gain gain={SynthOpts(GainEffectDef, props).gain}>{children}</gain>;

export const GainEffectDef: EffectDef<typeof GainEffectParams> = {
  label: "gain",
  params: GainEffectParams,
  Component: GainEffect,
};
//#endregion

//#region panner
export const PannerEffectParams = {
  pan: { type: "float", value: 0, min: -1, max: 1, automatable: true },
} satisfies ParamDict;

export const PannerEffect = (
  props: Partial<ParamDictProps<typeof PannerEffectParams>> & EffectProps,
  children: ChiElem,
) => <panner pan={SynthOpts(PannerEffectDef, props).pan}>{children}</panner>;

export const PannerEffectDef: EffectDef<typeof PannerEffectParams> = {
  label: "panner",
  params: PannerEffectParams,
  Component: PannerEffect,
};
//#endregion

//#region filter
export const FilterEffectParams = {
  frequency: { type: "float", value: 800, min: 20, max: 22050, automatable: true },
  q: { type: "float", value: 1, min: 0.001, max: 100, automatable: true },
  gain: { type: "float", value: 0, min: -40, max: 40, automatable: true },
  filterType: { type: "list", value: "lowpass", list: BiquadTypes } as ParamList<
    typeof BiquadTypes
  >,
} satisfies ParamDict;

export const FilterEffect = (
  props: Partial<ParamDictProps<typeof FilterEffectParams>> & EffectProps,
  children: ChiElem,
) => {
  const o = SynthOpts(FilterEffectDef, props);
  return (
    <biquad type={o.filterType} frequency={o.frequency} Q={o.q} gain={o.gain}>
      {children}
    </biquad>
  );
};

export const FilterEffectDef: EffectDef<typeof FilterEffectParams> = {
  label: "filter",
  params: FilterEffectParams,
  Component: FilterEffect,
};
//#endregion

//#region compressor
export const CompressorEffectParams = {
  threshold: { type: "float", value: -20, min: -60, max: 0, automatable: true },
  ratio: { type: "float", value: 4, min: 1, max: 50, automatable: true },
  attack: { type: "float", value: 1, min: 0, max: 1000, automatable: true },
  release: { type: "float", value: 250, min: 10, max: 2000, automatable: true },
  knee: { type: "float", value: 5, min: 0, max: 40, automatable: true },
  makeupGain: { type: "float", value: 1, min: 1, max: 100, automatable: true },
} satisfies ParamDict;

export const CompressorEffect = (
  props: Partial<ParamDictProps<typeof CompressorEffectParams>> & EffectProps,
  children: ChiElem,
) => {
  const o = SynthOpts(CompressorEffectDef, props);
  return (
    <gain gain={o.makeupGain}>
      <compressor
        threshold={o.threshold}
        ratio={o.ratio}
        attack={o.attack / 1000}
        release={o.release / 1000}
        knee={o.knee}
      >
        {children}
      </compressor>
    </gain>
  );
};

export const CompressorEffectDef: EffectDef<typeof CompressorEffectParams> = {
  label: "compressor",
  params: CompressorEffectParams,
  Component: CompressorEffect,
};
//#endregion

//#region telephonizer
export const TelephonizerEffectParams = {
  lowpass: { type: "float", value: 2000, min: 0, max: 4000, automatable: true },
  highpass: { type: "float", value: 500, min: 0, max: 1000, automatable: true },
} satisfies ParamDict;

export const TelephonizerEffect = (
  props: Partial<ParamDictProps<typeof TelephonizerEffectParams>> & EffectProps,
  children: ChiElem,
) => {
  const o = SynthOpts(TelephonizerEffectDef, props);
  return (
    <biquad type="highpass" frequency={o.highpass}>
      <biquad type="highpass" frequency={o.highpass}>
        <biquad type="lowpass" frequency={o.lowpass}>
          <biquad type="lowpass" frequency={o.lowpass}>{children}</biquad>
        </biquad>
      </biquad>
    </biquad>
  );
};

export const TelephonizerEffectDef: EffectDef<typeof TelephonizerEffectParams> = {
  label: "telephonizer",
  params: TelephonizerEffectParams,
  Component: TelephonizerEffect,
};
//#endregion

//#region delay
export const DelayEffectParams = {
  delayTime: { type: "float", value: 100, min: 20, max: 1000, automatable: false },
  feedback: { type: "float", value: 0.45, min: 0, max: 0.9, automatable: true },
  cutoff: { type: "float", value: 20000, min: 20, max: 20000, automatable: true },
  wetLevel: { type: "float", value: 0.5, min: 0, max: 1, automatable: true },
  dryLevel: { type: "float", value: 1, min: 0, max: 1, automatable: true },
} satisfies ParamDict;

export const DelayEffect = (
  props: Partial<ParamDictProps<typeof DelayEffectParams>> & EffectProps,
  children: ChiElem,
) => {
  const o = SynthOpts(DelayEffectDef, props);
  return (
    <DryWet
      dry={o.dryLevel}
      wet={o.wetLevel}
      effect={(input) => (
        <Feedback
          loop={(x) => (
            <gain gain={o.feedback}>
              <biquad type="lowpass" frequency={o.cutoff}>
                <delay delayTime={o.delayTime / 1000} maxDelay={10}>{x}</delay>
              </biquad>
            </gain>
          )}
        >
          {input}
        </Feedback>
      )}
    >
      {children}
    </DryWet>
  );
};

export const DelayEffectDef: EffectDef<typeof DelayEffectParams> = {
  label: "delay",
  params: DelayEffectParams,
  Component: DelayEffect,
};
//#endregion

//#region ping pong delay
export const PingPongDelayEffectParams = {
  delayTimeLeft: { type: "float", value: 150, min: 1, max: 10000, automatable: false },
  delayTimeRight: { type: "float", value: 200, min: 1, max: 10000, automatable: false },
  feedback: { type: "float", value: 0.5, min: 0, max: 0.9, automatable: true },
  wetLevel: { type: "float", value: 0.5, min: 0, max: 1, automatable: true },
} satisfies ParamDict;

export const PingPongDelayEffect = (
  props: Partial<ParamDictProps<typeof PingPongDelayEffectParams>> & EffectProps,
  children: ChiElem,
) => {
  const o = SynthOpts(PingPongDelayEffectDef, props);
  const id = useId("pingpong");
  return (
    <DryWet
      wet={o.wetLevel}
      effect={(input) => (
        <merger channels={2}>
          <delay id={`${id}.l`} inlet={0} delayTime={o.delayTimeLeft / 1000} maxDelay={10}>
            <splitter channels={2}>{input}</splitter>
            <gain gain={o.feedback}>
              <use id={`${id}.r`} />
            </gain>
          </delay>
          <delay id={`${id}.r`} inlet={1} delayTime={o.delayTimeRight / 1000} maxDelay={10}>
            <use id={`${id}.l`} />
          </delay>
        </merger>
      )}
    >
      {children}
    </DryWet>
  );
};

export const PingPongDelayEffectDef: EffectDef<typeof PingPongDelayEffectParams> = {
  label: "ping pong delay",
  params: PingPongDelayEffectParams,
  Component: PingPongDelayEffect,
};
//#endregion

//#region tremolo
export const TremoloEffectParams = {
  rate: { type: "float", value: 5, min: 0.1, max: 11, automatable: false },
  intensity: { type: "float", value: 0.3, min: 0, max: 1, automatable: false },
  stereoPhase: { type: "float", value: 0, min: 0, max: 180, automatable: false },
} satisfies ParamDict;

export const TremoloEffect = (
  props: Partial<ParamDictProps<typeof TremoloEffectParams>> & EffectProps,
  children: ChiElem,
) => {
  const o = SynthOpts(TremoloEffectDef, props);
  return (
    <Split
      each={(channel, i) => (
        <gain gain={0}>
          {channel}
          <mod param="gain">
            <Lfo
              rate={o.rate}
              offset={1 - o.intensity / 2}
              depth={o.intensity}
              phase={i === 0 ? 0 : (o.stereoPhase * Math.PI) / 180}
            />
          </mod>
        </gain>
      )}
    >
      {children}
    </Split>
  );
};

export const TremoloEffectDef: EffectDef<typeof TremoloEffectParams> = {
  label: "tremolo",
  params: TremoloEffectParams,
  Component: TremoloEffect,
};
//#endregion

//#region chorus
export const ChorusEffectParams = {
  rate: { type: "float", value: 1.5, min: 0.01, max: 8, automatable: false },
  depth: { type: "float", value: 0.7, min: 0, max: 1, automatable: false },
  delay: { type: "float", value: 0.0045, min: 0, max: 1, automatable: false },
  feedback: { type: "float", value: 0.4, min: 0, max: 0.95, automatable: false },
} satisfies ParamDict;

export const ChorusDelay = (delay: number) => 0.0002 * (Math.pow(10, delay) * 2);

export const ChorusEffect = (
  props: Partial<ParamDictProps<typeof ChorusEffectParams>> & EffectProps,
  children: ChiElem,
) => {
  const o = SynthOpts(ChorusEffectDef, props);
  const id = useId("chorus");
  const cd = ChorusDelay(o.delay);
  return (
    <DryWet
      effect={(input) => (
        <Split
          each={(channel, i) => (
            <delay id={`${id}.${i}`} delayTime={cd} maxDelay={1}>
              {channel}
              <gain gain={o.feedback}>
                <use id={`${id}.${1 - i}`} />
              </gain>
              <mod param="delayTime">
                <Lfo
                  rate={o.rate}
                  offset={cd}
                  depth={o.depth * cd}
                  phase={i === 0 ? 0 : Math.PI / 2}
                />
              </mod>
            </delay>
          )}
        >
          {input}
        </Split>
      )}
    >
      <gain gain={0.6934}>{children}</gain>
    </DryWet>
  );
};

export const ChorusEffectDef: EffectDef<typeof ChorusEffectParams> = {
  label: "chorus",
  params: ChorusEffectParams,
  Component: ChorusEffect,
};
//#endregion

//#region phaser
export const PhaserEffectParams = {
  rate: { type: "float", value: 0.1, min: 0, max: 8, automatable: false },
  depth: { type: "float", value: 0.6, min: 0, max: 1, automatable: false },
  feedback: { type: "float", value: 0.7, min: 0, max: 1, automatable: false },
  stereoPhase: { type: "float", value: 40, min: 0, max: 180, automatable: false },
  baseModulationFrequency: { type: "float", value: 700, min: 500, max: 1500, automatable: false },
} satisfies ParamDict;

const PhaserStages = 4;

export const PhaserEffect = (
  props: Partial<ParamDictProps<typeof PhaserEffectParams>> & EffectProps,
  children: ChiElem,
) => {
  const o = SynthOpts(PhaserEffectDef, props);
  const id = useId("phaser");
  const base = o.baseModulationFrequency;
  const allpasses = (input: ChiNode, i: number) => {
    const lfo = `${id}.lfo${i}`;
    return Array.from({ length: PhaserStages }, (_, s) => s).reduce<ChiNode>(
      (x, s) => (
        <biquad type="allpass" frequency={0} Q={10}>
          {x}
          <mod param="frequency">
            {s === 0
              ? (
                <Lfo
                  id={lfo}
                  rate={o.rate}
                  offset={base}
                  depth={o.depth * base}
                  phase={i === 0 ? 0 : (o.stereoPhase * Math.PI) / 180}
                />
              )
              : <use id={lfo} />}
          </mod>
        </biquad>
      ),
      input,
    );
  };
  return (
    <DryWet
      effect={(input) => (
        <Split
          each={(channel, i) => (
            <Feedback gain={o.feedback} delay={0} loop={(x) => allpasses(x, i)}>
              {channel}
            </Feedback>
          )}
        >
          {input}
        </Split>
      )}
    >
      {children}
    </DryWet>
  );
};

export const PhaserEffectDef: EffectDef<typeof PhaserEffectParams> = {
  label: "phaser",
  params: PhaserEffectParams,
  Component: PhaserEffect,
};
//#endregion

//#region overdrive
export const OverdriveEffectParams = {
  drive: { type: "float", value: 0.197, min: 0, max: 1, automatable: true },
  outputGain: { type: "float", value: -9.154, min: -46, max: 0, automatable: true },
  curveAmount: { type: "float", value: 0.979, min: 0, max: 1, automatable: false },
  algorithmIndex: { type: "int", value: 0, min: 0, max: 5, automatable: false },
} satisfies ParamDict;

const OverdriveTableSize = 8192;

const OverdriveAlgorithms: ((amount: number, table: Float32Array) => void)[] = [
  (amount, t) => {
    amount = Math.min(amount, 0.9999);
    const k = (2 * amount) / (1 - amount);
    for (let i = 0; i < OverdriveTableSize; i++) {
      const x = (i * 2) / OverdriveTableSize - 1;
      t[i] = ((1 + k) * x) / (1 + k * Math.abs(x));
    }
  },
  (_amount, t) => {
    for (let i = 0; i < OverdriveTableSize; i++) {
      const x = (i * 2) / OverdriveTableSize - 1;
      const y = (0.5 * Math.pow(x + 1.4, 2) - 1) * (x + 1.4 >= 0 ? 5.8 : 1.2);
      t[i] = Math.tanh(y);
    }
  },
  (amount, t) => {
    const a = 1 - amount;
    for (let i = 0; i < OverdriveTableSize; i++) {
      const x = (i * 2) / OverdriveTableSize - 1;
      const y = x < 0 ? -Math.pow(Math.abs(x), a + 0.04) : Math.pow(x, a);
      t[i] = Math.tanh(y * 2);
    }
  },
  (amount, t) => {
    const a = 1 - amount > 0.99 ? 0.99 : 1 - amount;
    for (let i = 0; i < OverdriveTableSize; i++) {
      const x = (i * 2) / OverdriveTableSize - 1;
      const abx = Math.abs(x);
      let y: number;
      if (abx < a) y = abx;
      else if (abx > a) y = a + (abx - a) / (1 + Math.pow((abx - a) / (1 - a), 2));
      else y = abx;
      t[i] = Math.sign(x) * y * (1 / ((a + 1) / 2));
    }
  },
  (_amount, t) => {
    for (let i = 0; i < OverdriveTableSize; i++) {
      const x = (i * 2) / OverdriveTableSize - 1;
      if (x < -0.08905) {
        t[i] = (-3 / 4) *
            (1 - Math.pow(1 - (Math.abs(x) - 0.032857), 12) +
              (1 / 3) * (Math.abs(x) - 0.032847)) +
          0.01;
      } else if (x < 0.320018) {
        t[i] = -6.153 * x * x + 3.9375 * x;
      } else {
        t[i] = 0.630035;
      }
    }
  },
  (amount, t) => {
    const bits = Math.round(Math.pow(2, 2 + Math.round(amount * 14) - 1));
    for (let i = 0; i < OverdriveTableSize; i++) {
      const x = (i * 2) / OverdriveTableSize - 1;
      t[i] = Math.round(x * bits) / bits;
    }
  },
];

export function OverdriveCurve(algorithmIndex: number, curveAmount: number): Float32Array {
  const table = new Float32Array(OverdriveTableSize);
  OverdriveAlgorithms[algorithmIndex]?.(curveAmount, table);
  return table;
}

export const OverdriveEffect = (
  props: Partial<ParamDictProps<typeof OverdriveEffectParams>> & EffectProps,
  children: ChiElem,
) => {
  const o = SynthOpts(OverdriveEffectDef, props);
  const curve = useMemo(
    () => OverdriveCurve(o.algorithmIndex, o.curveAmount),
    [o.algorithmIndex, o.curveAmount],
  );
  return (
    <gain gain={DbToLinear(o.outputGain)}>
      <shaper curve={curve}>
        <gain gain={o.drive}>{children}</gain>
      </shaper>
    </gain>
  );
};

export const OverdriveEffectDef: EffectDef<typeof OverdriveEffectParams> = {
  label: "overdrive",
  params: OverdriveEffectParams,
  Component: OverdriveEffect,
};
//#endregion

//#region convolver
export const ImpulsePaths = [
  "assets/impulses/Sweetspot1M.wav",
  "assets/impulses/cardiod-rear-levelled.wav",
  "assets/impulses/impulse_guitar.wav",
  "assets/impulses/impulse_rev.wav",
  "assets/impulses/irHall.ogg",
  "assets/impulses/ir_rev_short.wav",
  "assets/impulses/s2_r4_bd.wav",
  "assets/impulses/tim-stretch2.wav",
] as const;

const ImpulseAssets = Object.fromEntries(ImpulsePaths.map((p) => [p, p]));

export const CabinetEffectParams = {
  makeupGain: { type: "float", value: 1, min: 0, max: 20, automatable: true },
  impulsePath: { type: "list", value: ImpulsePaths[0], list: ImpulsePaths } as ParamList<
    typeof ImpulsePaths
  >,
} satisfies ParamDict;

export const CabinetEffect = (
  props: Partial<ParamDictProps<typeof CabinetEffectParams>> & EffectProps,
  children: ChiElem,
) => {
  const o = SynthOpts(CabinetEffectDef, props);
  return (
    <gain gain={o.makeupGain}>
      <convolver buffer={props.assets?.[o.impulsePath] ?? null}>{children}</convolver>
    </gain>
  );
};

export const CabinetEffectDef: EffectDef<typeof CabinetEffectParams> = {
  label: "cabinet",
  params: CabinetEffectParams,
  Component: CabinetEffect,
  assets: ImpulseAssets,
};

export const ConvolverEffectParams = {
  impulsePath: { type: "list", value: ImpulsePaths[0], list: ImpulsePaths } as ParamList<
    typeof ImpulsePaths
  >,
  wetLevel: { type: "float", value: 1, min: 0, max: 1, automatable: true },
  dryLevel: { type: "float", value: 1, min: 0, max: 1, automatable: true },
  highCut: { type: "float", value: 22050, min: 20, max: 22050, automatable: true },
  lowCut: { type: "float", value: 20, min: 20, max: 22050, automatable: true },
  level: { type: "float", value: 1, min: 0, max: 1, automatable: true },
} satisfies ParamDict;

export const ConvolverEffect = (
  props: Partial<ParamDictProps<typeof ConvolverEffectParams>> & EffectProps,
  children: ChiElem,
) => {
  const o = SynthOpts(ConvolverEffectDef, props);
  return (
    <DryWet
      dry={o.dryLevel}
      wet={o.wetLevel}
      effect={(input) => (
        <gain gain={o.level}>
          <convolver buffer={props.assets?.[o.impulsePath] ?? null}>
            <biquad type="highpass" frequency={o.lowCut}>
              <biquad type="lowpass" frequency={o.highCut}>{input}</biquad>
            </biquad>
          </convolver>
        </gain>
      )}
    >
      {children}
    </DryWet>
  );
};

export const ConvolverEffectDef: EffectDef<typeof ConvolverEffectParams> = {
  label: "convolver",
  params: ConvolverEffectParams,
  Component: ConvolverEffect,
  assets: ImpulseAssets,
};
//#endregion

//#region worklets
export const BitcrusherEffectParams = {
  bits: { type: "int", value: 4, min: 1, max: 16, automatable: false },
  normfreq: { type: "float", value: 0.1, min: 0.0001, max: 1, automatable: false },
} satisfies ParamDict;

export const BitcrusherEffect = (
  props: Partial<ParamDictProps<typeof BitcrusherEffectParams>> & EffectProps,
  children: ChiElem,
) => {
  const o = SynthOpts(BitcrusherEffectDef, props);
  return (
    <worklet name="bitcrusher" params={{ bits: o.bits, normfreq: o.normfreq }}>
      {children}
    </worklet>
  );
};

export const BitcrusherEffectDef: EffectDef<typeof BitcrusherEffectParams> = {
  label: "bitcrusher",
  params: BitcrusherEffectParams,
  Component: BitcrusherEffect,
  worklets: ["scripts/bitcrusher.js"],
};

export const MoogFilterEffectParams = {
  cutoff: { type: "float", value: 0.065, min: 0.0001, max: 1, automatable: false },
  resonance: { type: "float", value: 3.5, min: 0, max: 4, automatable: false },
} satisfies ParamDict;

export const MoogFilterEffect = (
  props: Partial<ParamDictProps<typeof MoogFilterEffectParams>> & EffectProps,
  children: ChiElem,
) => {
  const o = SynthOpts(MoogFilterEffectDef, props);
  return (
    <worklet name="MoogFilter" params={{ cutoff: o.cutoff, resonance: o.resonance }}>
      {children}
    </worklet>
  );
};

export const MoogFilterEffectDef: EffectDef<typeof MoogFilterEffectParams> = {
  label: "moog filter",
  params: MoogFilterEffectParams,
  Component: MoogFilterEffect,
  worklets: ["scripts/moog.js"],
};

export const WahWahEffectParams = {
  automode: { type: "boolean", value: true, automatable: false },
  baseFrequency: { type: "float", value: 0.153, min: 0.0001, max: 1, automatable: false },
  excursionOctaves: { type: "float", value: 3.3, min: 1, max: 6, automatable: false },
  sweep: { type: "float", value: 0.35, min: 0, max: 1, automatable: false },
  resonance: { type: "float", value: 19, min: 1, max: 100, automatable: false },
  sensitivity: { type: "float", value: -0.5, min: -1, max: 1, automatable: false },
  attackTime: { type: "float", value: 0.003, min: 0, max: 0.5, automatable: false },
  releaseTime: { type: "float", value: 0.5, min: 0.001, max: 0.5, automatable: false },
} satisfies ParamDict;

const WahWahOptions: AudioWorkletNodeOptions = { numberOfOutputs: 1 };

export const WahWahEffect = (
  props: Partial<ParamDictProps<typeof WahWahEffectParams>> & EffectProps,
  children: ChiElem,
) => {
  const { automode, ...o } = SynthOpts(WahWahEffectDef, props);
  return (
    <worklet name="WahWah" options={WahWahOptions} params={{ ...o, automode: automode ? 1 : 0 }}>
      {children}
    </worklet>
  );
};

export const WahWahEffectDef: EffectDef<typeof WahWahEffectParams> = {
  label: "wah wah",
  params: WahWahEffectParams,
  Component: WahWahEffect,
  worklets: ["scripts/wahwah.js"],
};
//#endregion

//#region registry
export const LiveEffects = {
  filter: FilterEffectDef,
  bitcrusher: BitcrusherEffectDef,
  cabinet: CabinetEffectDef,
  gain: GainEffectDef,
  panner: PannerEffectDef,
  compressor: CompressorEffectDef,
  telephonizer: TelephonizerEffectDef,
  delay: DelayEffectDef,
  overdrive: OverdriveEffectDef,
  moogFilter: MoogFilterEffectDef,
  tremolo: TremoloEffectDef,
  chorus: ChorusEffectDef,
  pingPongDelay: PingPongDelayEffectDef,
  phaser: PhaserEffectDef,
  convolver: ConvolverEffectDef,
  wahWah: WahWahEffectDef,
} satisfies Record<string, EffectDef<any>>;

export type LiveEffectType = keyof typeof LiveEffects;
export const LiveEffectTypes = Object.keys(LiveEffects) as LiveEffectType[];

export interface EffectSlot {
  id: string;
  type: LiveEffectType;
  opts?: Record<string, unknown>;
  bypassed?: boolean;
}

export interface EffectChainProps extends EffectProps {
  slots: EffectSlot[];
}

export const EffectChain = (props: EffectChainProps, children: ChiElem) =>
  props.slots.filter((slot) => !slot.bypassed).reduce<ChiNode>(
    (input, slot) =>
      hh(
        LiveEffects[slot.type].Component as EffectDef["Component"],
        { ...slot.opts, key: slot.id, assets: props.assets },
        input,
      ),
    <>{children}</>,
  );

export function EffectWorklets(types: LiveEffectType[]): string[] {
  return [...new Set(types.flatMap((type) => (LiveEffects[type] as EffectDef).worklets ?? []))];
}

export function EffectAssets(types: LiveEffectType[]): Record<string, string> {
  return Object.assign({}, ...types.map((type) => (LiveEffects[type] as EffectDef).assets ?? {}));
}
//#endregion
